import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { API_BASE_URL, SOCKET_LISTENER_EVENTS } from '@techchange/components';

import { ENUMS_VIDEO } from '../constants';

const axiosClient = axios.create({
	withCredentials: true,
	baseURL: API_BASE_URL.VIDEOS,
});

/**
 * React hook that keeps track of a video for a given ID.
 * Handles real-time updates if a socket is provided.
 *
 * @param {String|null} videoId   The ID of the video to fetch
 * @param {String} scope          The scope for the request
 * @param {String} scopeId        The scope id for the request
 * @param {Object} options        The options for the hook
 * @param {Object} options.socket The socket for real-time video updates, if any
 *
 * @returns {Object} -
 * @returns {Object} -.video          The video data
 * @returns {Object} -.error          The error, if encountered
 * @returns {Boolean} -.isLoading     Flag indicating if the request is loading
 */
const useVideo = (videoId, scope, scopeId, options = {}) => {

	const [video, setVideo] = useState(null);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	// Set initial video state
	useEffect(() => {

		if (!videoId) { return; }

		setIsLoading(true);
		setError(null);

		const params = {
			scope,
			scope_id: scopeId,
		};

		axiosClient.get(`/${videoId}`, { params })
			.then((response) => {
				setVideo(response.data);
			})
			.catch((err) => {
				setError(err.message);
				console.error(err);
			})
			.finally(() => setIsLoading(false));

	}, [videoId, scope, scopeId]);

	// Reset the video state if the videoId is nullified
	useEffect(() => {
		if (!videoId) {
			setIsLoading(false);
			setError(null);
			setVideo(null);
		}
	}, [videoId]);

	// Listen for real-time updates to the video if there is a socket
	useEffect(() => {

		if (!options.socket || !videoId) { return () => {}; }

		const onRealTimeChange = async (updatedVideo) => {

			// Check to make sure that the real-time update applies to the current video
			if (
				updatedVideo.id === videoId &&
				updatedVideo.scope === scope &&
				updatedVideo.scope_id === scopeId
			) {

				// If the video status is ready, re-fetch the video as a direct
				// request must be sent to retrieve the required tokens for playback
				// Additionally, this will return the public playback ID if the user is an admin
				if (updatedVideo.status === ENUMS_VIDEO.STATUS.READY) {

					const params = {
						scope,
						scope_id: scopeId,
					};

					axiosClient.get(`/${videoId}`, { params })
						.then((response) => {
							setVideo(response.data);
						})
						.catch((err) => {
							setError(err.message);
							console.error(err);
						});

				} else {
					setVideo(updatedVideo);
				}

			}

		};

		// Listen for real-time updates to the video
		options.socket.on(SOCKET_LISTENER_EVENTS.UPDATE_VIDEO, onRealTimeChange);

		// Clean up the listener for real-time video
		return () => {
			options.socket.removeListener(SOCKET_LISTENER_EVENTS.UPDATE_VIDEO, onRealTimeChange);
		};

	}, [videoId, scope, scopeId, options.socket]);

	return {
		video,
		error,
		isLoading,
	};

};

export default useVideo;
